import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import Cache from "../utils/cache";

import ProductTile from "../components/ProductTile";
import BankOfferModel from "../components/BankOfferModal";
import { normalizeVendorName, isVendorMatch } from "../utils/vendorMatchUtils";
import queryString from "query-string";

const ITEMS_PER_PAGE = 20;

export default function SpecialOfferPage() {
    const { offerType } = useParams();

    const [offers, setOffers] = useState([]);
    const [, setBankOffers] = useState([]);
    const [banksLogos, setBanksLogos] = useState({});
    const [banksAddresses, setBanksAddresses] = useState({});
    const [matchedBankOfferMap, setMatchedBankOfferMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedBankOffer, setSelectedBankOffer] = useState(null);

    // Pagination & sorting state
    const [currentPage, setCurrentPage] = useState(1);
    const [sortOrder, setSortOrder] = useState(() => {
        const params = queryString.parse(window.location.search);
        return typeof params.sort === "string" ? params.sort : "bestDiscount";
    });


    const initialPageSetRef = useRef(false);
    const navigate = useNavigate();
    const routerLocation = useRouterLocation(); // renamed to avoid eslint warning
    const prevSortOrderRef = useRef(null);

    // Sync page and sortOrder from URL on initial load
    useEffect(() => {
        if (initialPageSetRef.current) return;

        const params = queryString.parse(routerLocation.search);
        const pageFromUrl = parseInt(params.page || "1", 10);
        const sortFromUrl = typeof params.sort === "string" ? params.sort : "bestDiscount";

        if (!isNaN(pageFromUrl) && pageFromUrl > 0) {
            setCurrentPage(pageFromUrl);
        }

        // ✅ Sync both sortOrder and prevSortOrderRef correctly
        setSortOrder(sortFromUrl);
        prevSortOrderRef.current = sortFromUrl;

        initialPageSetRef.current = true;
    }, [routerLocation.search]);


    // Update URL when page or sortOrder changes
    useEffect(() => {
        const params = queryString.parse(routerLocation.search);
        let needUpdate = false;

        if (parseInt(params.page || "1", 10) !== currentPage) {
            params.page = currentPage;
            needUpdate = true;
        }
        if ((params.sort || "newest") !== sortOrder) {
            params.sort = sortOrder;
            needUpdate = true;
        }

        if (needUpdate) {
            const newSearch = queryString.stringify(params);
            navigate({ search: newSearch }, { replace: true });
        }
    }, [currentPage, sortOrder, navigate, routerLocation.search]);



    useEffect(() => {
        async function loadData() {
            setLoading(true);
            setError(null);

            try {
                const cachedData = Cache.getData() || {};

                // Load product offers from cache or Firestore
                let productOffers = cachedData.productOffers || [];
                if (!productOffers.length) {
                    const snap = await getDocs(collection(db, "product_offers"));
                    let allOffers = [];
                    snap.docs.forEach((doc) => {
                        const data = doc.data();
                        if (Array.isArray(data.offers)) {
                            allOffers.push(
                                ...data.offers.map((offer) => ({
                                    ...offer,
                                    id: `${doc.id}_${offer.offerId || Math.random().toString(36).slice(2, 8)}`,
                                    uploadedDttm: offer.uploadedDttm || data.uploadedDttm || null,
                                }))
                            );
                        }
                    });
                    productOffers = allOffers;
                    Cache.setData({ ...cachedData, productOffers });
                }

                // Load bank offers from cache or Firestore
                let banks = cachedData.bankOffers || [];
                if (!banks.length) {
                    const snapBanks = await getDocs(collection(db, "bank_offers"));
                    let allBanks = [];
                    snapBanks.docs.forEach((doc) => {
                        const bankName = doc.id;
                        const data = doc.data();
                        if (Array.isArray(data.offers)) {
                            allBanks.push(
                                ...data.offers.map((offer, idx) => ({
                                    ...offer,
                                    bankName,
                                    id: `${bankName}_offer_${idx}`,
                                }))
                            );
                        }
                    });
                    banks = allBanks;
                    Cache.setData({ ...Cache.getData(), bankOffers: banks });
                }
                setBankOffers(banks);

                // Load banks meta (logos and addresses) from cache or Firestore
                let meta = cachedData.banksMeta || {};
                if (!Object.keys(meta).length) {
                    const banksSnap = await getDocs(collection(db, "banks"));
                    const banksMetaObj = {};
                    banksSnap.forEach((doc) => {
                        const data = doc.data();
                        banksMetaObj[doc.id.toLowerCase()] = {
                            logo: data.logo || "",
                            address: data.address || "",
                        };
                    });
                    meta = banksMetaObj;
                    Cache.setData({ ...Cache.getData(), banksMeta: meta });
                }

                const banksLogoMap = {};
                const banksAddrMap = {};
                Object.entries(meta).forEach(([key, data]) => {
                    banksLogoMap[key] = data.logo || "";
                    banksAddrMap[key] = data.address || "";
                });
                setBanksLogos(banksLogoMap);
                setBanksAddresses(banksAddrMap);

                const getCentralStartOfDay = (d) => {
                    const formatter = new Intl.DateTimeFormat("en-US", {
                        timeZone: "America/Chicago",
                        year: "numeric",
                        month: "numeric",
                        day: "numeric",
                    });

                    const parts = formatter.formatToParts(d);
                    const year = +parts.find(p => p.type === "year").value;
                    const month = +parts.find(p => p.type === "month").value - 1;
                    const day = +parts.find(p => p.type === "day").value;

                    // Midnight Central Time == 6 AM UTC or 5 AM UTC depending on DST
                    // We return UTC date that aligns with midnight CST/CDT
                    return new Date(Date.UTC(year, month, day, 6));
                };


                const toCentralDate = (d) => {
                    const localTime = d.toLocaleString("en-US", { timeZone: "America/Chicago" });
                    return new Date(localTime);
                };



                const now = new Date();
                const nowCT = getCentralStartOfDay(now);

                const filteredOffers = productOffers.filter((offer) => {
                    // Parse Firestore timestamps
                    const startDate = offer.startDttm?.toDate
                        ? offer.startDttm.toDate()
                        : new Date(offer.startDttm || 0);
                    const endDate = offer.endDttm?.toDate
                        ? offer.endDttm.toDate()
                        : new Date(offer.endDttm || 0);
                    const uploadedDate = offer.uploadedDttm?.toDate
                        ? offer.uploadedDttm.toDate()
                        : new Date(offer.uploadedDttm || 0);

                    // Convert to CST/CDT equivalent time
                    const startCT = toCentralDate(startDate);
                    const endCT = toCentralDate(endDate);
                    const uploadedCTStart = getCentralStartOfDay(uploadedDate);

                    const active = startCT <= toCentralDate(now) && toCentralDate(now) <= endCT;
                    const uploadedToday = uploadedCTStart.getTime() === nowCT.getTime();

                    return active && uploadedToday;
                });



                // Match bank offers to product offers by normalized vendor name
                const bankMap = {};
                filteredOffers.forEach((p) => {
                    if (!p.vendor) return;
                    const pNorm = normalizeVendorName(p.vendor);
                    const matches = banks.filter((b) => {
                        if (!b.vendor) return false;
                        const bNorm = normalizeVendorName(b.vendor);
                        return isVendorMatch(bNorm, pNorm);
                    });
                    if (matches.length > 0) {
                        bankMap[p.id] = matches;
                    }
                });

                setOffers(filteredOffers);
                setMatchedBankOfferMap(bankMap);
            } catch (e) {
                setError("Failed to load todays offers: " + e.message);
            } finally {
                setLoading(false);
            }
        }
        loadData();
    }, [offerType]);

    const sortedOffers = useMemo(() => {
        if (!offers) return [];
        const copy = [...offers];

        const getDate = (o) =>
            o.uploadedDttm?.toDate ? o.uploadedDttm.toDate() : new Date(0);

        const getPrice = (o) => {
            const price = parseFloat(o.offerPrice);
            return isNaN(price) ? Infinity : price;
        };

        const getDiscount = (o) => {
            const offerPrice = parseFloat(o.offerPrice);
            const originalPrice = parseFloat(o.originalPrice);

            // Special case: free offers
            if (!isNaN(offerPrice) && offerPrice === 0) return Infinity;

            if (isNaN(offerPrice) || offerPrice <= 0) return null;
            if (isNaN(originalPrice) || originalPrice <= 0) return null;

            return (originalPrice - offerPrice) / originalPrice;
        };

        copy.sort((a, b) => {
            switch (sortOrder) {
                case "priceLow":
                    return getPrice(a) - getPrice(b);

                case "priceHigh":
                    return getPrice(b) - getPrice(a);

                case "bestDiscount": {
                    const discA = getDiscount(a);
                    const discB = getDiscount(b);

                    // Case 1: both have discounts (including free as Infinity)
                    if (discA !== null && discB !== null) {
                        if (discB !== discA) return discB - discA;
                        return getDate(b) - getDate(a);
                    }

                    // Case 2: neither has discount → sort by price asc, tie → newest
                    if (discA === null && discB === null) {
                        if (getPrice(a) !== getPrice(b)) return getPrice(a) - getPrice(b);
                        return getDate(b) - getDate(a);
                    }

                    // Case 3: one has discount → discount first
                    if (discA !== null && discB === null) return -1;
                    if (discA === null && discB !== null) return 1;

                    return 0;
                }

                default: // newest first
                    return getDate(b) - getDate(a);
            }
        });

        return copy;
    }, [offers, sortOrder]);



    // Pagination: slice sorted offers
    const totalPages = Math.ceil(sortedOffers.length / ITEMS_PER_PAGE);
    const paginatedOffers = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return sortedOffers.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [sortedOffers, currentPage]);

    useEffect(() => {
        if (currentPage < 1) {
            setCurrentPage(1);
        } else if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const onSortChange = (newSort) => {
        if (newSort !== sortOrder) {
            setSortOrder(newSort);
        }
    };


    if (loading)
        return (
            <div className="flex justify-center items-center h-64">
                <div className="loader"></div>
                <style>{`
          .loader {
            border: 6px solid #f3f3f3;
            border-top: 6px solid #3b82f6;
            border-radius: 50%;
            width: 48px;
            height: 48px;
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
            </div>
        );

    if (error) return <p className="text-red-600 text-center mt-10">{error}</p>;

    if (!offers.length)
        return (
            <div className="text-center text-gray-600 mt-10">
                <p className="text-4xl mb-2">😞</p>
                <p className="text-lg font-semibold">
                    No New offers uplaoded today.
                </p>
                <p>Please check back later.</p>
            </div>
        );

    return (
        <div className="max-w-6xl mx-auto p-6">
            <h1 className="text-3xl font-bold mb-6 text-center text-indigo-700 drop-shadow-md tracking-wide">
                Today's New Offers
            </h1>

            {/* Sort dropdown */}
            <div className="mb-4 flex justify-end items-center gap-4">
                <label htmlFor="sortOrder" className="font-semibold">
                    Sort By:
                </label>
                <select
                    id="sortOrder"
                    value={sortOrder}
                    onChange={(e) => onSortChange(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1"
                >
                    <option value="priceLow">Price: Low to High</option>
                    <option value="priceHigh">Price: High to Low</option>
                    <option value="bestDiscount">Best Discount</option>
                </select>
            </div>

            {/* Offers grid */}
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                {paginatedOffers.map((offer) => (
                    <ProductTile
                        key={offer.id}
                        offer={offer}
                        onClick={() => navigate(`/product/${offer.id}?page=${currentPage}&sort=${sortOrder}`)}
                        matchedBankOffers={matchedBankOfferMap[offer.id]}
                        banksLogos={banksLogos}
                        setSelectedBankOffer={setSelectedBankOffer}
                    />
                ))}
            </div>

            {/* Pagination controls */}
            <div className="flex justify-center mt-6 space-x-3">
                <button
                    className="px-3 py-1 border rounded disabled:opacity-50"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                    Previous
                </button>

                <span className="px-3 py-1 border rounded">
                    Page {currentPage} of {totalPages}
                </span>

                <button
                    className="px-3 py-1 border rounded disabled:opacity-50"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                >
                    Next
                </button>
            </div>

            {/* Bank offer modal */}
            {selectedBankOffer && (
                <BankOfferModel
                    offer={selectedBankOffer}
                    banksLogos={banksLogos}
                    banksAddresses={banksAddresses}
                    onClose={() => setSelectedBankOffer(null)}
                />
            )}
        </div>
    );
}
